import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import ProductSalesModal from '../components/products/ProductSalesModal';

function ProductUnitShow() {
    const { id } = useParams();
    const [unit, setUnit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSalesModal, setShowSalesModal] = useState(false);

    useEffect(() => {
        loadUnit();
    }, [id]);

    const loadUnit = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/product_units/${id}`);
            if (response.success) {
                setUnit(response.data);
            } else {
                setError(response.message || 'Failed to load unit');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Loading unit...</div>;
    if (error) return <div className="mt-16 p-8 text-center text-red-500 font-medium">{error}</div>;
    if (!unit) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Unit not found</div>;

    const statusColors = {
        'available': 'bg-emerald-100 text-emerald-800',
        'sold': 'bg-blue-100 text-blue-800',
        'reserved': 'bg-amber-100 text-amber-800'
    };

    return (
        <div className="mt-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-600/20">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">{unit.primary_identifier}</h1>
                        <div className="flex items-center gap-2 mt-1">
                            <Link to={`/products/${unit.product_id}`} className="text-indigo-600 hover:text-indigo-700 font-bold text-sm tracking-tight uppercase">
                                {unit.product_name}
                            </Link>
                            <span className="text-slate-300">•</span>
                            <span className="text-slate-500 text-xs font-mono">ID: #{unit.id}</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowSalesModal(true)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all uppercase tracking-widest shadow-lg shadow-indigo-200"
                    >
                        Manage Sales Details
                    </button>
                    <Link to="/product_units" className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-all uppercase tracking-widest shadow-sm">
                        Back to Units
                    </Link>
                </div>
            </div>

            {/* Product Sales Modal */}
            <ProductSalesModal
                isOpen={showSalesModal}
                onClose={() => setShowSalesModal(false)}
                productUnitId={id}
                invoiceItemId={unit.invoice_item_id}
                onSuccess={() => loadUnit()}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                        <h2 className="text-lg font-bold text-slate-800 mb-6 uppercase tracking-widest text-xs">Unit Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Status</label>
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusColors[unit.status] || 'bg-slate-100 text-slate-600'}`}>
                                        {unit.status}
                                    </span>
                                </div>
                                {unit.identifiers && unit.identifiers.length > 0 ? (
                                    unit.identifiers.map((ident: any, idx: number) => (
                                        <div key={idx}>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">{ident.name}</label>
                                            <p className={`font-mono text-slate-800 ${idx === 0 ? 'text-xl font-bold' : 'text-lg'}`}>{ident.value}</p>
                                        </div>
                                    ))
                                ) : (
                                    <>
                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Primary Identifier</label>
                                            <p className="text-xl font-mono font-bold text-slate-800">{unit.primary_identifier}</p>
                                        </div>
                                        {unit.secondary_identifier && (
                                            <div>
                                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Secondary Identifier</label>
                                                <p className="text-lg font-mono text-slate-700">{unit.secondary_identifier}</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Added On</label>
                                    <p className="text-slate-700 font-medium">{new Date(unit.created_at).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })}</p>
                                </div>
                                {unit.sold_at && (
                                    <div>
                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Sold On</label>
                                        <p className="text-rose-600 font-bold">{new Date(unit.sold_at).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                        <h2 className="text-lg font-bold text-slate-800 mb-6 uppercase tracking-widest text-xs">Timeline & Activities</h2>
                        <div className="text-center py-12 text-slate-400 font-medium border-2 border-dashed border-slate-100 rounded-2xl">
                            Activity logging coming soon
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-slate-900 rounded-3xl shadow-xl p-8 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-3xl -mr-16 -mt-16"></div>

                        <div className="relative z-10 text-center">
                            <h2 className="text-xs font-bold opacity-60 uppercase tracking-widest mb-6">Unit QR Label</h2>

                            <div className="bg-white p-3 rounded-2xl inline-block shadow-inner mb-8">
                                {unit.qr_code_url ? (
                                    <img src={unit.qr_code_url} alt="QR Code" className="w-40 h-40 block" />
                                ) : (
                                    <div className="w-40 h-40 bg-slate-100 flex items-center justify-center text-slate-400 text-xs text-center p-4">
                                        QR NOT AVAILABLE
                                    </div>
                                )}
                            </div>

                            <div className="space-y-3">
                                <button className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white hover:bg-slate-50 text-slate-900 rounded-xl font-bold text-xs uppercase tracking-widest transition-all shadow-lg">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                    Download PNG
                                </button>
                                <button className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-xs uppercase tracking-widest transition-all border border-white/20">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                    Print Label
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
                        <h3 className="indigo-900 font-bold text-xs mb-2 uppercase tracking-tight">Management Tip</h3>
                        <p className="text-indigo-700 text-xs leading-relaxed">
                            Scanning this QR during inventory check or sale will immediately identify this specific unit's history.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProductUnitShow;
